import { PocTypography } from 'commons/components/components-wrapper';
import { InputValidity, Size } from '@one/web-components';
import { AssistiveText, ExtendedValidationMessages, SelectErrorsLayout } from 'commons/components/form-elements';
import { MAX_OPTIONS_WITHOUT_SEARCH } from 'core/constants/constants';
import { useTranslation } from 'core/i18n/i18n';
import React, { useState } from 'react';
import {
    SearchableDropdownCustomSearchInput,
    SearchableDropdownDefaultSearchInput,
    SearchableDropdownList,
} from '../components';
import {
    SearchableDropdownComponent,
    SearchableDropdownLayout,
    SearchableDropdownWrapper,
    SearchBarLayout,
} from '../searchable-dropdown.style';
import { CustomFilterSearch, NestedSelectableOptions, SelectableOptions } from '../searchable-dropdown.type';
import { useSearchBarFocusGuard } from '../hooks/use-search-bar-focus-guard.hook';
import { SearchableDropdownFooter } from './components';

interface SearchableDropdownProps {
    name: string;
    label?: string;
    inline?: boolean;
    options: SelectableOptions[] | NestedSelectableOptions[];
    fixedOptions?: SelectableOptions[];
    placeholder: string;
    value: string[];
    secondaryValue?: string[];
    searchable?: boolean;
    selectAllText?: string;
    validity?: InputValidity;
    validationMessages?: ExtendedValidationMessages;
    spaceForError?: boolean;
    floatingAssistiveText?: boolean;
    placeholderAsLabel?: boolean;
    isLoading?: boolean;
    searchPlaceholder?: string;
    disabled?: boolean;
    minWidth?: string;
    numberSelectedOptions?: number;
    customFilter?: CustomFilterSearch;
    required?: boolean;
    size?: Exclude<Size, 'xSmall' | 'large' | 'xLarge'>;
    includeFakeOption?: boolean;
    multiple?: boolean;
    virtualized?: boolean;
    expandGroupsByDefault?: boolean;
    disableEmptyGroups?: boolean;
    className?: string;
    onValueChange: (value: string[]) => void;
    onApplySelection: (value: string[]) => void;
    applyDisabled?: boolean;
    onClear?: VoidFunction;
    onClickOutside?: VoidFunction;
    onVisibleChange?: (visible: boolean) => void;
    onValidity?: (field: string, validity: InputValidity) => void;
}

export const SearchableDropdown: React.FC<SearchableDropdownProps> = (props) => {
    const {
        name,
        label,
        inline = false,
        options,
        fixedOptions = [],
        searchable = options.length > MAX_OPTIONS_WITHOUT_SEARCH,
        placeholder,
        placeholderAsLabel = true,
        selectAllText,
        value,
        secondaryValue,
        validity,
        validationMessages,
        spaceForError = false,
        floatingAssistiveText = false,
        isLoading,
        searchPlaceholder,
        numberSelectedOptions,
        minWidth,
        disabled = false,
        customFilter,
        size = 'medium',
        required = false,
        includeFakeOption = false,
        multiple = true,
        virtualized = false,
        expandGroupsByDefault = false,
        disableEmptyGroups = false,
        className,
        onValueChange,
        onClear,
        onApplySelection,
        applyDisabled = false,
        onClickOutside,
        onVisibleChange,
        onValidity,
    } = props;
    const isNestedList = options.some((option) => Object.hasOwn(option, 'children'));

    const [defaultFilter, setDefaultFilter] = useState<string>('');
    const [visible, setVisible] = useState<boolean>(false);
    const searchBarRef = useSearchBarFocusGuard();

    const { t } = useTranslation();

    const getDropdownClassname = () =>
        (visible && value.length) || (!secondaryValue && value.length) || secondaryValue?.length
            ? 'dropdown-filled'
            : 'dropdown-empty';

    const handleOnClear = () => {
        setVisible(false);
        onClear?.();
    };

    const inputValue = secondaryValue && !visible ? secondaryValue : value;
    const layoutClassName = ['searchable-dropdown', inline ? 'inline' : 'block', className].filter(Boolean).join(' ');

    return (
        <SearchableDropdownLayout className={layoutClassName}>
            {label ? (
                <PocTypography element="span" variant="subtitle1" className="label block">
                    {label}
                </PocTypography>
            ) : undefined}
            <SearchableDropdownWrapper>
                <SearchableDropdownComponent
                    name={name}
                    data-testid="searchable-dropdown"
                    variant="outlined"
                    size={size}
                    label={placeholderAsLabel ? placeholder : undefined}
                    multiple={multiple}
                    hideSelectAll
                    selectAllText={selectAllText}
                    value={inputValue}
                    placeholder={placeholder}
                    visible={visible}
                    disabled={!customFilter && (disabled || ![...fixedOptions, ...options].length)}
                    clearButtonProps={{ onclick: handleOnClear }}
                    validity={validity}
                    required={required}
                    className={getDropdownClassname()}
                    minWidth={minWidth}
                    menuProps={{ noAutoFocus: searchable, listProps: { multiple: true, showSelectorControl: true } }}
                    onValueChange={(ev) => onValueChange([...(ev.detail as string[])])}
                    onVisibleChange={(ev) => {
                        setVisible(ev.detail);
                        onVisibleChange?.(ev.detail);
                    }}
                    onClickOutside={onClickOutside}
                    onValidityChange={(ev) => onValidity?.(name, ev.detail)}
                >
                    {searchable && (
                        <SearchBarLayout ref={searchBarRef} data-testid="searchable-dropdown-search-bar">
                            {customFilter ? (
                                <SearchableDropdownCustomSearchInput
                                    searchFilter={customFilter.filter}
                                    setSearchFilter={customFilter.onChangeFilter}
                                    placeholder={searchPlaceholder}
                                    executeSearch={customFilter.onExecuteSearch}
                                    assistiveTextString={customFilter.assistiveTextString}
                                />
                            ) : (
                                <SearchableDropdownDefaultSearchInput
                                    filter={defaultFilter}
                                    setFilter={setDefaultFilter}
                                    placeholder={searchPlaceholder}
                                />
                            )}
                        </SearchBarLayout>
                    )}
                    <SearchableDropdownList
                        selectedValues={inputValue}
                        options={options}
                        fixedOptions={fixedOptions}
                        defaultFilter={defaultFilter}
                        isLoading={isLoading}
                        size={size}
                        includeFakeOption={includeFakeOption}
                        customFilter={customFilter}
                        isNestedList={isNestedList}
                        visible={visible}
                        virtualized={virtualized}
                        expandGroupsByDefault={expandGroupsByDefault}
                        disableEmptyGroups={disableEmptyGroups}
                    />
                    <SearchableDropdownFooter
                        value={value}
                        secondaryValue={secondaryValue}
                        isNestedList={isNestedList}
                        disableEmptyGroups={disableEmptyGroups}
                        options={options}
                        filter={defaultFilter}
                        numberSelectedOptions={numberSelectedOptions}
                        setVisible={setVisible}
                        onApplySelection={onApplySelection}
                        applyDisabled={applyDisabled}
                        onValueChange={onValueChange}
                        t={t}
                        setFilter={setDefaultFilter}
                    />
                </SearchableDropdownComponent>
                <SelectErrorsLayout
                    slot="assistive-text"
                    className={`${spaceForError ? 'space-for-error' : ''} ${
                        floatingAssistiveText ? 'floating-assistive-text' : ''
                    }`}
                >
                    <AssistiveText validity={validity} validationMessages={validationMessages} />
                </SelectErrorsLayout>
            </SearchableDropdownWrapper>
        </SearchableDropdownLayout>
    );
};
